import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, ArrowLeft, Check, Loader2, Mic2, Pause, Play, Plus, Search,
} from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { labApi } from '@/lib/api';

// Biblioteka głosów ElevenLabs (audio-first): przeglądasz publiczną Voice
// Library z filtrami (język/płeć/kategoria), odsłuchujesz i jednym klikiem
// dodajesz głos do konta - od tego momentu jest dostępny w produkcji reklam
// i można go sparować z postacią (Avatary UGC). Konto ElevenLabs = źródło
// prawdy; ta strona to wygodny frontend do niego.

interface AccountVoice {
  voiceId: string;
  name: string;
  description?: string;
  previewUrl?: string | null;
}

interface SharedVoice {
  voiceId: string;
  publicOwnerId: string;
  name: string;
  gender: string;
  age: string;
  accent: string;
  language: string;
  description: string;
  category: string;
  previewUrl: string | null;
}

const LANGUAGE_OPTIONS = [
  { value: 'pl', label: 'Polski' },
  { value: 'en', label: 'Angielski' },
  { value: 'de', label: 'Niemiecki' },
  { value: 'cs', label: 'Czeski' },
  { value: 'es', label: 'Hiszpański' },
  { value: 'fr', label: 'Francuski' },
  { value: 'it', label: 'Włoski' },
  { value: '', label: 'Wszystkie języki' },
];

const GENDER_FILTER_OPTIONS = [
  { value: '', label: 'Dowolna płeć' },
  { value: 'female', label: 'Kobieta' },
  { value: 'male', label: 'Mężczyzna' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'Wszystkie kategorie' },
  { value: 'high_quality', label: 'High quality' },
  { value: 'professional', label: 'Professional' },
];

export function LabVoicesPage() {
  const [accountVoices, setAccountVoices] = useState<AccountVoice[]>([]);
  const [accountLoading, setAccountLoading] = useState(true);
  const [accountError, setAccountError] = useState<string | null>(null);

  const [language, setLanguage] = useState('pl');
  const [gender, setGender] = useState('');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sharedVoices, setSharedVoices] = useState<SharedVoice[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);

  const [addTarget, setAddTarget] = useState<SharedVoice | null>(null);
  const [addName, setAddName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addedVoiceIds, setAddedVoiceIds] = useState<Set<string>>(new Set());

  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => () => {
    audioRef.current?.pause();
    audioRef.current = null;
  }, []);

  const loadAccountVoices = useCallback(async () => {
    try {
      const res = await labApi.getVideoProductionOptions('elevenlabs');
      setAccountVoices(res.data.data?.voices || []);
      setAccountError(null);
    } catch (err: any) {
      setAccountError(err?.response?.data?.error || 'Nie udało się pobrać głosów konta. Sprawdź integrację ElevenLabs w Integracje → Produkcja reklam AI.');
    } finally {
      setAccountLoading(false);
    }
  }, []);

  useEffect(() => { void loadAccountVoices(); }, [loadAccountVoices]);

  const browse = useCallback(async (nextPage: number) => {
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const res = await labApi.getSharedVoices({
        language: language || undefined,
        gender: gender || undefined,
        category: category || undefined,
        search: search.trim() || undefined,
        page: nextPage,
      });
      setSharedVoices(res.data.data?.voices || []);
      setHasMore(Boolean(res.data.data?.hasMore));
      setPage(nextPage);
    } catch (err: any) {
      setBrowseError(err?.response?.data?.error || 'Nie udało się pobrać biblioteki głosów. Voice Library wymaga płatnego planu ElevenLabs.');
    } finally {
      setBrowseLoading(false);
    }
  }, [language, gender, category, search]);

  // Pierwsze pobranie + reakcja na zmianę filtrów (wyszukiwarka na Enter/przycisk)
  useEffect(() => { void browse(0); }, [language, gender, category]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePreview = (id: string, previewUrl: string | null) => {
    if (!previewUrl) return;
    if (playingId === id) {
      audioRef.current?.pause();
      audioRef.current = null;
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(previewUrl);
    audio.onended = () => setPlayingId(current => (current === id ? null : current));
    audio.onerror = () => setPlayingId(current => (current === id ? null : current));
    audioRef.current = audio;
    setPlayingId(id);
    void audio.play().catch(() => setPlayingId(null));
  };

  const openAddModal = (voice: SharedVoice) => {
    setAddTarget(voice);
    setAddName(voice.name);
    setAddError(null);
  };

  const handleAdd = async () => {
    if (!addTarget) return;
    setAdding(true);
    setAddError(null);
    try {
      await labApi.addSharedVoice({
        publicOwnerId: addTarget.publicOwnerId,
        voiceId: addTarget.voiceId,
        name: addName.trim() || addTarget.name,
      });
      setAddedVoiceIds(prev => new Set(prev).add(addTarget.voiceId));
      setAddTarget(null);
      await loadAccountVoices();
    } catch (err: any) {
      setAddError(err?.response?.data?.error || 'Nie udało się dodać głosu do konta.');
    } finally {
      setAdding(false);
    }
  };

  const previewButton = (id: string, previewUrl: string | null) => (
    <button
      type="button"
      onClick={() => togglePreview(id, previewUrl)}
      disabled={!previewUrl}
      className="p-1.5 rounded-lg text-text-muted hover:text-accent-primary transition-colors disabled:opacity-30 shrink-0"
      title={playingId === id ? 'Zatrzymaj' : 'Odsłuchaj'}
      aria-label={playingId === id ? 'Zatrzymaj odsłuch' : 'Odsłuchaj głos'}
    >
      {playingId === id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="animate-fade-in">
        <Link to="/marketing/laboratorium" className="inline-flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Laboratorium
        </Link>
        <h1 className="text-3xl font-bold text-text-primary">Głosy</h1>
        <p className="text-text-secondary mt-2">
          Biblioteka głosów ElevenLabs: przeglądaj, odsłuchuj i dodawaj głosy do konta jednym klikiem. Dodany głos jest od razu dostępny w produkcji reklam i można go sparować z postacią w Avatarach UGC - jeden głos brzmi wtedy identycznie w hooku i wszystkich scenach.
        </p>
      </div>

      {/* Głosy na koncie */}
      <div className="bg-surface-raised rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 bg-surface-hover/40 border-b border-border-subtle flex items-center gap-2">
          <Mic2 className="w-4 h-4 text-accent-primary" />
          <span className="text-xs font-semibold text-text-primary">Głosy na koncie</span>
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-surface-raised border border-border-subtle text-text-secondary">
            {accountVoices.length}
          </span>
          <span className="text-[10px] text-text-muted">te głosy widzisz w modalach produkcji</span>
        </div>
        <div className="p-4">
          {accountLoading ? (
            <div className="flex items-center gap-2 py-4 text-xs text-text-muted">
              <Loader2 className="w-4 h-4 animate-spin" /> Ładowanie głosów konta...
            </div>
          ) : accountError ? (
            <div className="flex items-center gap-2 text-[11px] text-red-400">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {accountError}
            </div>
          ) : accountVoices.length === 0 ? (
            <p className="text-[11px] text-text-disabled">Brak głosów na koncie - dodaj pierwszy z biblioteki poniżej.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {accountVoices.map(voice => (
                <div key={voice.voiceId} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border-subtle bg-surface-hover/30">
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-medium text-text-primary block truncate">{voice.name}</span>
                    {voice.description && <span className="text-[10px] text-text-muted block truncate">{voice.description}</span>}
                  </div>
                  {previewButton(`account-${voice.voiceId}`, voice.previewUrl || null)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Przeglądarka Voice Library */}
      <div className="bg-surface-raised rounded-xl border border-border overflow-hidden">
        <div className="px-4 py-3 bg-surface-hover/40 border-b border-border-subtle flex items-center gap-2">
          <Search className="w-4 h-4 text-accent-info" />
          <span className="text-xs font-semibold text-text-primary">Przeglądaj bibliotekę ElevenLabs</span>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <Select value={language} onChange={e => setLanguage(e.target.value)}>
              {LANGUAGE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
            <Select value={gender} onChange={e => setGender(e.target.value)}>
              {GENDER_FILTER_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
            <Select value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORY_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Select>
            <form
              onSubmit={e => { e.preventDefault(); void browse(0); }}
              className="relative"
            >
              <Search className="w-3.5 h-3.5 text-text-disabled absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Szukaj po nazwie..."
                className="w-full text-xs bg-surface-hover border border-border rounded-lg pl-8 pr-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
              />
            </form>
          </div>

          {browseError && (
            <div className="flex items-center gap-2 text-[11px] text-red-400">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {browseError}
            </div>
          )}

          {browseLoading ? (
            <div className="flex items-center gap-2 py-8 justify-center text-xs text-text-muted">
              <Loader2 className="w-4 h-4 animate-spin" /> Przeszukiwanie biblioteki...
            </div>
          ) : sharedVoices.length === 0 && !browseError ? (
            <EmptyState
              size="sm"
              icon={Mic2}
              title="Brak głosów dla tych filtrów"
              description="Zmień język, płeć albo wyszukiwaną frazę."
            />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {sharedVoices.map(voice => {
                const alreadyAdded = addedVoiceIds.has(voice.voiceId);
                const meta = [voice.gender, voice.age, voice.accent, voice.language, voice.category]
                  .filter(Boolean).join(' · ');
                return (
                  <div key={voice.voiceId} className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-border-subtle bg-surface-hover/30">
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-medium text-text-primary block truncate">{voice.name}</span>
                      {meta && <span className="text-[10px] text-text-muted block truncate">{meta}</span>}
                      {voice.description && <span className="text-[10px] text-text-secondary block mt-1 line-clamp-2">{voice.description}</span>}
                    </div>
                    {previewButton(`shared-${voice.voiceId}`, voice.previewUrl)}
                    <button
                      type="button"
                      onClick={() => openAddModal(voice)}
                      disabled={alreadyAdded}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-accent-primary bg-accent-primary/10 border border-accent-primary/20 rounded-lg hover:bg-accent-primary/15 transition-all disabled:opacity-50 shrink-0"
                    >
                      {alreadyAdded ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      {alreadyAdded ? 'Dodany' : 'Dodaj'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {(page > 0 || hasMore) && !browseLoading && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void browse(page - 1)}
                disabled={page === 0}
                className="px-3 py-1.5 text-xs font-medium text-text-secondary bg-surface-hover border border-border rounded-lg hover:text-text-primary transition-colors disabled:opacity-40"
              >
                Poprzednia
              </button>
              <span className="text-[11px] text-text-muted">Strona {page + 1}</span>
              <button
                type="button"
                onClick={() => void browse(page + 1)}
                disabled={!hasMore}
                className="px-3 py-1.5 text-xs font-medium text-text-secondary bg-surface-hover border border-border rounded-lg hover:text-text-primary transition-colors disabled:opacity-40"
              >
                Następna
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={Boolean(addTarget)}
        onClose={() => setAddTarget(null)}
        title="Dodaj głos do konta"
        description={addTarget ? `Głos "${addTarget.name}" zostanie dodany do Twojego konta ElevenLabs i będzie dostępny w produkcji reklam.` : undefined}
        footer={(
          <div className="flex items-center justify-between w-full">
            <button
              onClick={handleAdd}
              disabled={adding || !addName.trim()}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
            >
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Dodaj głos
            </button>
            <button
              onClick={() => setAddTarget(null)}
              className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
            >
              Anuluj
            </button>
          </div>
        )}
      >
        <div className="space-y-2">
          <label htmlFor="voice-add-name" className="text-xs font-medium text-text-secondary block">
            Nazwa głosu w Twojej bibliotece
          </label>
          <input
            id="voice-add-name"
            type="text"
            value={addName}
            onChange={e => setAddName(e.target.value)}
            placeholder='np. "Kasia - UGC energiczna"'
            className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
          />
          <p className="text-[10px] text-text-muted">
            Nadaj opisową nazwę (osoba + zastosowanie), żeby łatwo wybierać głos w produkcji.
          </p>
          {addError && (
            <div className="flex items-center gap-2 text-[11px] text-red-400">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {addError}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
